import logger from '../utils/logger.js';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setClientMaintainerFlag } from '../utils/maintenanceBlock';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const getServerOrigin = () => window.location.origin;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const [loading, setLoading] = useState(true);

  // Computed property for authentication status
  const isAuthenticated = user !== null || isGuest;

  useEffect(() => {
    logger.info('🔐 Client AuthContext initializing...');
    const token = localStorage.getItem('token');
    const tokenOrigin = localStorage.getItem('tokenOrigin');
    const guestMode = localStorage.getItem('guestMode');
    const currentOrigin = getServerOrigin();
    
    logger.info('🔐 Token found:', token ? 'Yes' : 'No');
    logger.info('🔐 Guest mode:', guestMode);
    
    if (token && tokenOrigin && tokenOrigin !== currentOrigin) {
      logger.warn('🔐 Token was issued by a different server (' + tokenOrigin + ' vs ' + currentOrigin + '). Clearing stale token.');
      localStorage.removeItem('token');
      localStorage.removeItem('tokenOrigin');
      delete axios.defaults.headers.common['Authorization'];
      setClientMaintainerFlag(false);
      setLoading(false);
      return;
    }

    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuthStatus();
    } else if (guestMode === 'true') {
      setClientMaintainerFlag(false);
      logger.info('🔐 Setting guest mode');
      setIsGuest(true);
      setLoading(false);
    } else {
      setClientMaintainerFlag(false);
      logger.info('🔐 No authentication found');
      setLoading(false);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      logger.info('🔐 Checking auth status...');
      const response = await axios.get('/api/auth/me');
      logger.info('✅ Auth check successful:', response.data.user.email);
      const nextUser = response.data.user;
      setClientMaintainerFlag(nextUser?.maintainer === true);
      setUser(nextUser);
    } catch (error) {
      logger.error('❌ Auth check failed:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setClientMaintainerFlag(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      logger.info('🔐 Attempting login for:', email);
      const response = await axios.post('/api/auth/login', { email, password });
      const { token, user } = response.data;
      
      logger.info('✅ Login successful');
      localStorage.setItem('token', token);
      localStorage.setItem('tokenOrigin', getServerOrigin());
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setClientMaintainerFlag(user?.maintainer === true);
      setUser(user);
      setIsGuest(false);
      localStorage.removeItem('guestMode');
      
      return { success: true, user };
    } catch (error) {
      logger.error('❌ Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      logger.info('🔐 Attempting registration...');
      const response = await axios.post('/api/auth/register', userData);
      const { token, user } = response.data;
      
      logger.info('✅ Registration successful');
      localStorage.setItem('token', token);
      localStorage.setItem('tokenOrigin', getServerOrigin());
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setClientMaintainerFlag(user?.maintainer === true);
      setUser(user);
      setIsGuest(false);
      localStorage.removeItem('guestMode');
      
      return { success: true };
    } catch (error) {
      logger.error('❌ Registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Registration failed' 
      };
    }
  };

  // Guest login functionality commented out - guest access disabled
  /*
  const loginAsGuest = () => {
    logger.info('🔐 Logging in as guest');
    setIsGuest(true);
    setUser(null);
    localStorage.setItem('guestMode', 'true');
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    return { success: true };
  };
  */

  const logout = () => {
    logger.info('🔐 Logging out');
    localStorage.removeItem('token');
    localStorage.removeItem('tokenOrigin');
    localStorage.removeItem('guestMode');
    delete axios.defaults.headers.common['Authorization'];
    setClientMaintainerFlag(false);
    setUser(null);
    setIsGuest(false);
  };

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const response = await axios.get('/api/auth/me');
      if (response.data?.user) {
        setClientMaintainerFlag(response.data.user?.maintainer === true);
        setUser(response.data.user);
        return response.data.user;
      }
    } catch (error) {
      logger.error('❌ Failed to refresh user:', error);
    }
    return null;
  }, []);

  const value = {
    user,
    isGuest,
    isAuthenticated,
    loading,
    login,
    register,
    // loginAsGuest, // Commented out - guest access disabled
    logout,
    refreshUser,
  };

  logger.info('🔐 Auth context value:', { 
    hasUser: !!user, 
    isGuest, 
    isAuthenticated, 
    loading 
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
